import React, { useState } from 'react';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { adminAutomationService } from '../../services/adminAutomationService';
import { AutomationStep, AutomationAction } from '../../services/automationService';
import {
  buildActionStepEmailConfigUpdate,
  resolveActionStepEmailConfig,
} from '../../utils/actionStepEmailConfig';

interface QuickEditPopoverProps {
  step: AutomationStep;
  automationId: string;
  anchorRect: DOMRect;
  onClose: () => void;
  onSuccess: () => void;
}

const QuickEditPopover: React.FC<QuickEditPopoverProps> = ({ 
  step, 
  automationId, 
  anchorRect, 
  onClose, 
  onSuccess 
}) => {
  const { isDark } = useAdminTheme();
  const { showSuccess, showError } = useNotification();
  const [saving, setSaving] = useState(false);
  
  // State for editable fields
  const config = step.config as AutomationAction;
  const emailFields = resolveActionStepEmailConfig(step.config);
  const [title, setTitle] = useState(step.title);
  const [description, setDescription] = useState(step.description || '');
  const [delayValue, setDelayValue] = useState(config.config?.waitValue || 1);
  const [delayUnit, setDelayUnit] = useState(config.config?.waitType === 'fixed' ? 'hours' : 'hours'); // Defaulting for simplicity
  const [emailSubject, setEmailSubject] = useState(emailFields.subject);
  const [fromName, setFromName] = useState(emailFields.fromName);
  const [fromEmail, setFromEmail] = useState(emailFields.fromEmail);
  const [htmlBody, setHtmlBody] = useState(emailFields.htmlBody);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const existing = (config ?? {}) as unknown as Record<string, unknown>;
      let nextConfig: AutomationAction;

      if (step.type === 'delay') {
        const nested =
          existing.config && typeof existing.config === 'object'
            ? (existing.config as Record<string, unknown>)
            : {};
        nextConfig = {
          ...existing,
          config: {
            ...nested,
            waitValue: delayValue,
            waitType: delayUnit,
          },
        } as unknown as AutomationAction;
      } else if (step.type === 'action') {
        nextConfig = buildActionStepEmailConfigUpdate(existing, {
          subject: emailSubject,
          fromName,
          fromEmail,
          htmlBody,
        }) as unknown as AutomationAction;
      } else {
        nextConfig = existing as unknown as AutomationAction;
      }

      const updates: Partial<AutomationStep> = {
        title,
        description,
        config: nextConfig,
      };

      await adminAutomationService.updateAutomationStep(automationId, step.id, updates);
      showSuccess('Step updated successfully');
      onSuccess();
    } catch (error) {
      console.error('Error updating step:', error);
      showError('Failed to update step');
    } finally {
      setSaving(false);
    }
  };

  const commonVariables = [
    { tag: '{{first_name}}', label: 'User Name' },
    { tag: '{{passcode}}', label: 'Verification Code' },
    { tag: '{{email}}', label: 'User Email' },
    { tag: '{{city}}', label: 'User City' },
    { tag: '{{country}}', label: 'User Country' },
  ];

  // Calculate position (simplified for now, ideally use a positioning library like floating-ui)
  const style: React.CSSProperties = {
    position: 'fixed',
    top: anchorRect.top + anchorRect.height / 2,
    left: anchorRect.left + anchorRect.width + 10,
    zIndex: 1000,
    transform: 'translateY(-50%)',
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[999] bg-black/5" 
        onClick={onClose}
      ></div>

      {/* Popover Card */}
      <div 
        className={`w-[450px] max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border ${
          isDark 
            ? 'bg-gray-800 border-gray-700 shadow-purple-900/20 text-white' 
            : 'bg-white border-gray-200 shadow-gray-400/20 text-gray-900'
        } p-6`}
        style={style}
      >
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold text-sm uppercase tracking-wider text-purple-600">Edit System Content</h4>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">×</button>
        </div>

        <div className="space-y-4">
          {/* General Fields */}
          <div>
            <label className={`block text-[10px] font-bold uppercase mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Title
            </label>
            <input 
              type="text" 
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border text-sm ${
                isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'
              } focus:outline-none focus:ring-2 focus:ring-purple-500`}
            />
          </div>

          {/* Type-Specific Fields */}
          {step.type === 'delay' && (
            <div className="flex gap-2">
              <div className="flex-1">
                <label className={`block text-[10px] font-bold uppercase mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Amount
                </label>
                <input 
                  type="number" 
                  value={delayValue}
                  onChange={(e) => setDelayValue(Number(e.target.value))}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'
                  } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                />
              </div>
              <div className="flex-1">
                <label className={`block text-[10px] font-bold uppercase mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Unit
                </label>
                <select 
                  value={delayUnit}
                  onChange={(e) => setDelayUnit(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'
                  } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                >
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
          )}

          {step.type === 'action' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-[10px] font-bold uppercase mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    From Name
                  </label>
                  <input 
                    type="text" 
                    value={fromName}
                    onChange={(e) => setFromName(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${
                      isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'
                    } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  />
                </div>
                <div>
                  <label className={`block text-[10px] font-bold uppercase mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    From Email
                  </label>
                  <input 
                    type="text" 
                    value={fromEmail}
                    onChange={(e) => setFromEmail(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${
                      isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'
                    } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  />
                </div>
              </div>

              <div>
                <label className={`block text-[10px] font-bold uppercase mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Email Subject
                </label>
                <input 
                  type="text" 
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'
                  } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={`block text-[10px] font-bold uppercase ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    HTML Body (Code View)
                  </label>
                  <div className="flex gap-2">
                    {commonVariables.map(v => (
                      <button 
                        key={v.tag}
                        onClick={() => setHtmlBody(prev => prev + v.tag)}
                        title={v.label}
                        className="text-[10px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded hover:bg-purple-200"
                      >
                        {v.tag.replace(/[{}]/g, '')}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea 
                  value={htmlBody}
                  onChange={(e) => setHtmlBody(e.target.value)}
                  rows={10}
                  className={`w-full px-3 py-2 rounded-lg border text-xs font-mono leading-relaxed ${
                    isDark ? 'bg-gray-900 border-gray-600 text-purple-300' : 'bg-gray-50 border-gray-200'
                  } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  placeholder="<!DOCTYPE html>..."
                />
              </div>
            </>
          )}

          <button 
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 bg-purple-600 text-white rounded-xl font-bold text-sm hover:bg-purple-700 transition shadow-lg shadow-purple-900/10"
          >
            {saving ? 'Saving...' : 'Apply Changes'}
          </button>
        </div>
      </div>
    </>

  );
};

export default QuickEditPopover;
